import React, { useState, useMemo } from "react";
import { ButtonComp, CardComp } from "../../../../components";
import { AiOutlineClose } from "react-icons/ai";
import { formatMoney, getMemberTransactionChargesTotal } from "../../../../utils";
import moment from "moment";
import { usePayMemberLoanRepaymentMutation } from "../../../../store/member/member-loan";
import toast from "react-hot-toast";
import { BsInfoCircle } from "react-icons/bs";
import { useGetMemberPaymentFeePreviewQuery } from "../../../../store/member/member-contribution";
import {
  getFrontendBaseUrl,
  redirectToMemberOnlinePayment,
} from "../../../../utils/memberOnlinePayment";

const MemberLoanRepaymentPayModal = ({
  repaymentDetails,
  loanRequestId,
  setModalPage,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const [payRepayment, { isLoading: isPaying }] =
    usePayMemberLoanRepaymentMutation();

  const repaymentAmount = useMemo(() => {
    if (!repaymentDetails?.amount) return 0;
    const amount = parseFloat(repaymentDetails.amount);
    return Math.round(amount * 100) / 100;
  }, [repaymentDetails]);

  const { data: feePreview } = useGetMemberPaymentFeePreviewQuery(
    { principal_amount: repaymentAmount },
    { skip: !repaymentAmount || repaymentAmount <= 0 }
  );

  const handlePayment = async () => {
    if (!repaymentDetails || !loanRequestId) {
      toast.error("Invalid repayment details");
      return;
    }

    try {
      setIsProcessing(true);

      // Don't send amount - backend should fetch it from repayment record for security
      const payload = {
        loan_request_id: parseInt(loanRequestId),
        repayment_id: repaymentDetails.id,
        frontend_base_url: getFrontendBaseUrl(),
      };

      const response = await payRepayment(payload).unwrap();

      // Check if payment URL is returned (Paystack redirect)
      if (response?.data?.payment_url || response?.data?.authorization_url) {
        const paymentReference = response?.data?.payment_reference;

        // Store payment reference for confirmation page
        if (paymentReference) {
          sessionStorage.setItem(
            "pending_loan_repayment_reference",
            paymentReference
          );
        }

        toast.success("Redirecting to payment page...");
        if (!redirectToMemberOnlinePayment(response)) {
          toast.error("Payment URL not received. Please try again.");
        }
      } else {
        // If no payment URL, payment might have been processed immediately (tokenized card)
        if (response?.status) {
          toast.success(
            response?.message || "Loan repayment processed successfully"
          );
          setModalPage();
          // Refresh the page or refetch data
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error(
            response?.message || "Failed to process payment. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          "An error occurred. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!repaymentDetails) {
    return (
      <div className="relative p-4">
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="absolute top-4 right-4 cursor-pointer"
          size={24}
          style={{ color: "var(--color-primary)" }}
        />
        <div className="text-center py-5">
          <p className="text-muted">No repayment details available</p>
        </div>
      </div>
    );
  }

  // Use the repayment's due_at (specific repayment installment due date)
  // NOT the loan's payback_at (total loan payback date)
  const repaymentDueDate = repaymentDetails?.due_at || null;
  
  const dueDate = repaymentDueDate
    ? moment(repaymentDueDate).format("DD-MMM-YYYY")
    : "N/A";

  const isOverdue = repaymentDueDate
    ? moment(repaymentDueDate).isBefore(moment(), "day")
    : false;

  return (
    <div className="relative p-4" style={{ maxWidth: "600px", width: "100%" }}>
      <AiOutlineClose
        onClick={() => setModalPage()}
        className="absolute top-4 right-4 cursor-pointer"
        size={24}
        style={{ color: "var(--color-primary)" }}
      />
      <h4 className="fw-bold mb-4 text-primary1 text-center">
        Pay Loan Repayment
      </h4>

      <CardComp cardClassName="border-0 shadow-sm mb-4">
        <div className="p-4">
          <div className="mb-4">
            <h6 className="fw-semibold text-black1 mb-3">Repayment Details</h6>
            <div className="row g-3">
              <div className="col-6">
                <small className="text-muted d-block mb-1">Amount</small>
                <span className="fw-bold text-primary1 text-lg">
                  {formatMoney(repaymentAmount)}
                </span>
              </div>
              <div className="col-6">
                <small className="text-muted d-block mb-1">Due Date</small>
                <span
                  className={`fw-bold ${
                    isOverdue ? "text-danger" : "text-black1"
                  }`}
                >
                  {dueDate}
                </span>
                {isOverdue && (
                  <div className="mt-1">
                    <span className="badge bg-danger text-white small">
                      Overdue
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="p-3 rounded"
            style={{ backgroundColor: "#f7f7f7" }}
          >
            <div className="d-flex align-items-start gap-2">
              <BsInfoCircle
                size={20}
                style={{ color: "var(--color-secondary)", marginTop: "2px", flexShrink: 0 }}
              />
              <div>
                <p className="mb-1 small fw-semibold text-black1">
                  Payment Method
                </p>
                <p className="mb-0 small text-muted">
                  You will be redirected to Paystack to complete the payment
                  securely. Your payment will be processed immediately upon
                  confirmation.
                </p>
              </div>
            </div>
            <div className="border-top mt-3 pt-3">
              <div className="d-flex justify-content-between mb-2">
                <small className="text-muted">Transaction charges</small>
                <small className="fw-semibold">
                  {formatMoney(getMemberTransactionChargesTotal(feePreview))}
                </small>
              </div>
              <div className="d-flex justify-content-between pt-2 border-top">
                <span className="fw-semibold">Total to Pay</span>
                <span className="fw-bold text-primary1">
                  {formatMoney(feePreview?.member_payable_total || repaymentAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardComp>

      <div className="d-flex justify-content-between gap-3">
        <ButtonComp
          btnText="Cancel"
          btnClassName="border border-primary1 text-primary1 px-4 py-2 rounded bg-white"
          onClick={() => setModalPage()}
          disabled={isPaying || isProcessing}
        />
        <ButtonComp
          btnText={isPaying || isProcessing ? "Processing..." : "Pay Now"}
          btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
          onClick={handlePayment}
          loading={isPaying || isProcessing}
          disabled={isPaying || isProcessing}
        />
      </div>
    </div>
  );
};

export default MemberLoanRepaymentPayModal;

